import { showError } from '@calendly/alert';
import { parseQuery } from '@calendly/http-tools';
import { I18n } from '@calendly/i18n';
import { Badge } from '@calendly/ui/components/badge';
import { Button } from '@calendly/ui/components/button';
import { Loader } from '@calendly/ui/components/loader';
import { Sheet } from '@calendly/ui/components/sheet';
import { BodyText, HeadingText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { useEffect, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { setGetParams } from 'actions/core/navigation';
import { useGetUserQuery } from 'common';
import { connectOrganizationZoomAccount, useGetZoomPermissionRequestQuery } from 'features/integrations';
import CalendlyLogoSvg from './calendly-logo-full';
import ZoomLogoSvg from './zoom-logo';
import ZoomSuccessSvg from './zoom-success';
const useSearchParams = () => {
  const location = useLocation();
  return parseQuery(location.search);
};
const containerCss = "c74l5ns";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const logoCss = "l5rud1p";
const Logo = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(logoCss, props.className)} />;
});
const contentCss = "c1hhj21d";
const Content = forwardRef((props, ref) => <Sheet ref={ref} {...props} className={classNames(contentCss, props.className)} />);
const calendlyLogoCss = "cg2xnc";
const CalendlyLogo = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'img';
  const src = CalendlyLogoSvg;
  const alt = 'Calendly';
  return <Component ref={ref} {...restProps} src={src} alt={alt} className={classNames(calendlyLogoCss, props.className)} />;
});
const headerCss = "h1url3ah";
const Header = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerCss, props.className)} />;
});
const bannerCss = "b18hl7rz";
const Banner = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(bannerCss, props.className)} />;
});
const zoomLogoCss = "zlm683a";
const ZoomLogo = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'img';
  const src = ZoomLogoSvg;
  const alt = 'Zoom';
  return <Component ref={ref} {...restProps} src={src} alt={alt} className={classNames(zoomLogoCss, props.className)} />;
});
const zoomSuccessLogoCss = "zkbnj3z";
const ZoomSuccessLogo = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'img';
  const src = ZoomSuccessSvg;
  const alt = 'Zoom';
  return <Component ref={ref} {...restProps} src={src} alt={alt} className={classNames(zoomSuccessLogoCss, props.className)} />;
});
const sectionCss = "s1fo9lc";
const Section = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(sectionCss, props.className)} />;
});
const organizationCardCss = "o1s490u0";
const OrganizationCard = forwardRef((props, ref) => <Sheet ref={ref} {...props} className={classNames(organizationCardCss, props.className)} />);
const listCss = "l1rrza9z";
const List = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'ol';
  return <Component ref={ref} {...restProps} className={classNames(listCss, props.className)} />;
});
const styledListItemCss = "sdcjebe";
const StyledListItem = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'li';
  return <Component ref={ref} {...restProps} className={classNames(styledListItemCss, props.className)} />;
});
const centeredCss = "c1smn88m";
const Centered = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(centeredCss, props.className)} />;
});
const ErrorState = () => <>
    <HeadingText>{I18n.t('errors.default_no_action')}</HeadingText>
    <div>
      <Button decoration="secondary-outline" spacingRight={4} onClick={() => {
      window.location.href = '/';
    }}>
        {I18n.t('publisher.admin.navigation.back_button')}
      </Button>
      <Button onClick={() => {
      window.location.reload();
    }}>
        {I18n.t('publisher.action.reload_page')}
      </Button>
    </div>
  </>;
const PermissionRequestInfo = ({
  owner
}) => {
  const dispatch = useDispatch();
  return <>
      <Header>
        <HeadingText level={4} variant="eyebrow">
          {I18n.t('publisher.integrations.zoom.org.landing.eyebrow_title')}
        </HeadingText>
        <HeadingText level={3}>
          {I18n.t('publisher.integrations.zoom.org.landing.title')}
        </HeadingText>
      </Header>
      <Banner>
        <ZoomLogo />
      </Banner>
      <Section>
        <BodyText weight="bold">
          {I18n.t('publisher.integrations.zoom.org.landing.calendly_account')}
        </BodyText>
        <OrganizationCard>
          <div>
            <BodyText>
              {owner}
            </BodyText>
            <BodyText color="muted" size="medium">
              {I18n.t('publisher.integrations.zoom.org.landing.org_owner')}
            </BodyText>
          </div>
          <Badge type="new">
            {I18n.t('publisher.integrations.zoom.org.landing.requested')}
          </Badge>
        </OrganizationCard>
      </Section>
      <Section>
        <BodyText weight="bold">
          {I18n.t('publisher.integrations.zoom.org.landing.steps_to_connect')}
        </BodyText>
        <List>
          <StyledListItem>
            {I18n.t('publisher.integrations.zoom.org.landing.steps.login')}
            <BodyText color="muted" size="medium">
              {I18n.t('publisher.integrations.zoom.org.landing.steps.admin')}
            </BodyText>
          </StyledListItem>
          <StyledListItem>{I18n.t('publisher.integrations.zoom.org.landing.steps.activate')}</StyledListItem>
        </List>
      </Section>
      <Centered>
        <Button onClick={() => dispatch(connectOrganizationZoomAccount())}>
          {I18n.t('publisher.integrations.zoom.org.landing.connect_button')}
        </Button>
      </Centered>
    </>;
};
const PermissionRequestPage = ({
  permissionRequestId
}) => {
  const {
    data,
    isFetching,
    isError
  } = useGetZoomPermissionRequestQuery(permissionRequestId);
  const dispatch = useDispatch();
  const params = useSearchParams();
  useEffect(() => {
    if (params.error === 'invalid_csrf') {
      dispatch(setGetParams({
        error: null
      }));
      dispatch(showError(I18n.t('errors.server.server')));
    }
  }, [dispatch, params.error]);
  return <>
      {isFetching && <Loader />}
      {isError && <ErrorState />}
      {data && <PermissionRequestInfo owner={data.organizationOwner} />}
    </>;
};
const ResultPage = ({
  isSuccess
}) => {
  const {
    data,
    isFetching
  } = useGetUserQuery();
  const isAuthenticated = !!data?.id;
  const redirectPath = isAuthenticated ? '/integrations/zoom' : '/';
  if (isFetching) {
    return <Loader />;
  }
  if (!isSuccess) {
    return <ErrorState />;
  }
  return <>
      <HeadingText level={1}>
        {I18n.t('publisher.integrations.zoom.org.success.success')}
      </HeadingText>
      <Banner>
        <ZoomSuccessLogo />
      </Banner>
      <BodyText>
        {I18n.t('publisher.integrations.zoom.org.success.text')}
      </BodyText>
      <Centered>
        <Button onClick={() => {
        window.location.href = redirectPath;
      }}>
          {I18n.t('publisher.integrations.zoom.org.success.go_to_calendly')}
        </Button>
      </Centered>
    </>;
};
export const ZoomAdminConsent = () => {
  const params = useSearchParams();
  return <Container>
      <Logo>
        <CalendlyLogo />
      </Logo>

      <Content>
        {params.success ? <ResultPage isSuccess={params.success === 'true'} /> : <PermissionRequestPage permissionRequestId={params.id} />}
      </Content>
    </Container>;
};

require("./zoom_admin_consent.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./zoom_admin_consent.js");